import React, { useState } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Monogram from "../ui/Monogram";
import "./PortfolioDetails.css";

/**
 * PortfolioDetails Component
 * Displays detailed information about a portfolio item
 */
const PortfolioDetails = ({ portfolioItems = [] }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const portfolioItem = portfolioItems.find((item) => item.id === id);

  // Slider handlers
  const nextSlide = () => {
    if (portfolioItem && portfolioItem.images) {
      setCurrentSlide((prev) =>
        prev === portfolioItem.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevSlide = () => {
    if (portfolioItem && portfolioItem.images) {
      setCurrentSlide((prev) =>
        prev === 0 ? portfolioItem.images.length - 1 : prev - 1
      );
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  if (!portfolioItem) {
    return (
      <section id="portfolio-details" className="portfolio-details">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div>
            <div className="text-center">
              <h2>{t("Portfolio.details.notFound")}</h2>
              <button
                onClick={() => navigate("/#portfolio")}
                className="mt-3 inline-flex items-center justify-center rounded-full bg-brand-600 px-8 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700"
              >
                {t("Portfolio.details.backToPortfolio")}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="portfolio-details" className="portfolio-details">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <div className="flex items-center justify-between">
            <h2>{t("Portfolio.details.title")}</h2>
            <ol>
              <li>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/");
                  }}
                >
                  {t("Menu.Home")}
                </a>
              </li>
              <li>
                <a
                  href="/#portfolio"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/#portfolio");
                  }}
                >
                  {t("Menu.Portfolio")}
                </a>
              </li>
              <li>{t("Portfolio.details.title")}</li>
            </ol>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Image Gallery */}
          <div className="lg:col-span-2">
            <div className="portfolio-details-slider">
              <div className="swiper-wrapper">
                {portfolioItem.images && portfolioItem.images.length > 0 ? (
                  portfolioItem.images.map((image, index) => (
                    <div
                      key={index}
                      className={`swiper-slide ${
                        index === currentSlide ? "active" : ""
                      }`}
                      style={{
                        display: index === currentSlide ? "block" : "none",
                      }}
                    >
                      <img
                        src={image}
                        alt={`${portfolioItem.title} - ${index + 1}`}
                        className="h-auto max-w-full"
                      />
                    </div>
                  ))
                ) : (
                  <div className="swiper-slide active">
                    <Monogram name={portfolioItem.title} className="aspect-video w-full rounded-lg" />
                  </div>
                )}
              </div>

              {/* Navigation Arrows */}
              {portfolioItem.images && portfolioItem.images.length > 1 && (
                <>
                  <button
                    className="swiper-button-prev"
                    onClick={prevSlide}
                    aria-label={t("Portfolio.details.previousSlide")}
                  >
                    <i className="bx bx-chevron-left"></i>
                  </button>
                  <button
                    className="swiper-button-next"
                    onClick={nextSlide}
                    aria-label={t("Portfolio.details.nextSlide")}
                  >
                    <i className="bx bx-chevron-right"></i>
                  </button>

                  {/* Pagination Dots */}
                  <div className="swiper-pagination">
                    {portfolioItem.images.map((_, index) => (
                      <span
                        key={index}
                        className={`swiper-pagination-bullet ${
                          index === currentSlide
                            ? "swiper-pagination-bullet-active"
                            : ""
                        }`}
                        onClick={() => goToSlide(index)}
                        aria-label={t("Portfolio.details.goToSlide", {
                          number: index + 1,
                        })}
                      ></span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Project Information */}
          <div className="lg:col-span-1">
            <div className="portfolio-info">
              <h3>{t("Portfolio.details.information")}</h3>
              <ul>
                <li>
                  <strong>{t("Portfolio.details.category")}</strong>:{" "}
                  {portfolioItem.category}
                </li>
                <li>
                  <strong>{t("Portfolio.details.client")}</strong>:{" "}
                  {portfolioItem.client}
                </li>
                <li>
                  <strong>{t("Portfolio.details.date")}</strong>:{" "}
                  {portfolioItem.date}
                </li>
                <li>
                  <strong>{t("Portfolio.details.url")}</strong>:{" "}
                  <a
                    href={`https://${portfolioItem.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {portfolioItem.url}
                  </a>
                </li>
              </ul>
            </div>
            <div className="portfolio-description">
              <h2>{portfolioItem.title}</h2>
              <p>{portfolioItem.description}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

PortfolioDetails.propTypes = {
  portfolioItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      category: PropTypes.string.isRequired,
      client: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      images: PropTypes.arrayOf(PropTypes.string),
    })
  ),
};

export default PortfolioDetails;
